import {unwrapResult} from '@reduxjs/toolkit';
import {Form as AntForm} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import React, {
    useEffect, useMemo, useState, FC,
} from 'react';

import {dispatchFormWatcherEvent} from 'components/form/hooks/use-form-watcher';
import {
    SignAndFileListUploader,
} from 'components/form/inputs/uploader-list/sign-and-file-list-uploader/sign-and-file-list-uploader';
import {RequestCardContext} from 'components/request/replies/context/request-card-context';
import {RequestFormFields} from 'components/request/request-form/components/request-from-fields/request-form-fields';
import {requestMainFields} from 'components/request/request-form/request-form.constants';
import {StatusModelAwareActions} from 'components/request/status-model-aware-actions/status-model-aware-actions';
import {convertRequestData} from 'components/request/utils/request-api-utils';
import {
    loadMetadata, selectMetadata, IFieldMeta,
} from 'entities/metadata';
import {normalizeFormDataForRequest} from 'modules/data/utils/data.utils';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages, EErrorMessages} from 'shared/constants/messages';
import {
    URL_REQUESTS,
    URL_REQUESTS_REQUIREMENT_PROLONGATION_MESSAGE_UPDATE,
} from 'shared/constants/urls';
import {IFileDto} from 'shared/types/files';
import {showMessage, transformEntityNameToClassName} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {selectRequestData} from 'store/slices/request-slice/request-selectors';
import {requestSliceActions} from 'store/slices/request-slice/request-slice';
import {
    loadRequestData,
    loadRequestStatusesData,
    saveRequestData,
} from 'store/slices/request-slice/request-slice-thunks';

import s from './response-request.module.less';

interface IResponseRequestProps {
    entityName: string;
    data?: any;
    requestFormKey: string;
    requestCommentId?: string;
    isRequestAdditionalPlacementType?: boolean;
    requestType?: string;
    hasPermissionDisplayFileComponent: boolean;
    onClose?: () => void;
}

export const ResponseRequest: FC<IResponseRequestProps> = ({
    entityName,
    data,
    onClose,
    requestFormKey,
    requestCommentId,
    isRequestAdditionalPlacementType,
    requestType,
    hasPermissionDisplayFileComponent,
}:IResponseRequestProps) => {
    const dispatch = useAppDispatch();
    const [isEditingForm, setEditingForm] = useState(false);
    const [fileList, setFileList] = useState<IFileDto[] | undefined>(data?.attachments);
    const [messageCollapseOpen, setMessageCollapseOpen] = useState(false);
    const requestData = useAppSelector(selectRequestData);
    const metadata = useAppSelector(selectMetadata(entityName, EEntityType.FORM));
    const requestId = requestData?.requests?.id;
    const [form] = useForm<IFieldMeta[]>();
    const attachmentList = !!data?.attachments?.length;
    const formData = convertRequestData(requestData, data);
    const {resetRequestStatusesData} = requestSliceActions;
    const hasDisplayFileListUploader = hasPermissionDisplayFileComponent && (
        !isRequestAdditionalPlacementType && (attachmentList || isEditingForm)
    );

    const contextValue = useMemo(() => ({
        text: messageCollapseOpen ? 'Свернуть всю переписку' : 'Развернуть всю переписку',
        isEditing: isEditingForm || false,
        handleCollapse: () => setMessageCollapseOpen(v => !v),
        setEditing: () => setEditingForm(v => !v),
    }), [messageCollapseOpen, isEditingForm]);

    useEffect(() => {
        form.setFieldsValue({...data, ...formData});
        setFileList(data?.attachments);
    }, [form, data]);

    useEffect(() => {
        if (!metadata && data) {
            dispatch(loadMetadata(entityName, EEntityType.FORM));
        }
    }, [metadata, data, entityName, dispatch]);

    const handleFinish = (values: any) => {
        if (requestCommentId && requestId) {
            form.validateFields().then(() => {
                dispatch(saveRequestData({
                    commentId: requestCommentId,
                    url: URL_REQUESTS_REQUIREMENT_PROLONGATION_MESSAGE_UPDATE,
                    data: normalizeFormDataForRequest(values, [...metadata?.fields, ...requestMainFields]),
                })).then(unwrapResult).then(() => {
                    form.resetFields();
                    setEditingForm(!isEditingForm);
                    showMessage({message: ECommonMessages.SAVE_SUCCESS});
                    dispatch(resetRequestStatusesData);
                    dispatch(loadRequestStatusesData({commentId: requestCommentId, requestFormKey}));
                    dispatch(loadRequestData({id: requestId, url: URL_REQUESTS}));
                }, error => {
                    showMessage({
                        message: error?.response?.data ?? EErrorMessages.UNEXPECTED_ERROR_OCCURRED,
                        isError: true,
                    });
                });
            });
        }
    };

    return (
        <>
            <div className={cn(s['response-request__actions'])}>
                <RequestCardContext.Provider value={contextValue}>
                    <StatusModelAwareActions
                        requestFormKey={requestFormKey}
                        requestCommentId={requestCommentId}
                        onClose={onClose}
                        requestType={requestType}
                        entityName={entityName}
                        onSubmit={() => {
                            form.submit();
                        }}
                        requestInfo={requestData?.requests}
                    />
                </RequestCardContext.Provider>
            </div>
            {
                data && requestCommentId && (
                    <AntForm
                        layout="vertical"
                        form={form}
                        className="form"
                        onFinish={handleFinish}
                        onValuesChange={(_, values: any) => {
                            dispatchFormWatcherEvent({values, targetForm: form});
                        }}
                    >
                        <div className={cn(
                            'form-body',
                            {
                                [transformEntityNameToClassName(entityName)]: entityName,
                            },
                        )}
                        >
                            <RequestFormFields
                                isEditable={isEditingForm}
                                entityName={entityName}
                                data={formData}
                                fields={metadata?.fields}
                                form={form}

                            />
                            <AntForm.Item
                                name="files"
                                style={{width: '100%'}}
                            >
                                {hasDisplayFileListUploader && (
                                    <SignAndFileListUploader
                                        disabled={!isEditingForm}
                                        fileList={fileList}
                                        onChange={setFileList}
                                        requestCommentId={requestCommentId}
                                        dataKeyForDownload="id"
                                        url={URL_REQUESTS}
                                        signUrl={URL_REQUESTS}
                                        isDownloadDocumentList={!!data?.attachments?.length}
                                    />
                                )}
                            </AntForm.Item>
                        </div>
                    </AntForm>
                )
            }
        </>
    );
};
